import { useState } from "react";
import { Icon } from "../components/Icon";
import { EmptyState, PageHeader, Panel } from "../components/ui";
import { useI18n } from "../i18n";
import { sitePath, vehiclesUnder } from "../lib/fleet";
import { canManageFleet, isTenantAdmin } from "../lib/permissions";
import { useSession, type TenantData } from "../state/session";
import { SiteAlertsPanel } from "./SiteAlerts";
import { SiteChecklistPanel } from "./SiteChecklist";
import { SiteGeofencesPanel } from "./SiteGeofences";
import { DeleteSiteDialog, SiteFormDialog } from "./SiteForm";
import { VehicleFormDialog } from "./VehicleForm";
import { SitesTable } from "./SitesTable";
import { VehicleTable } from "./VehicleTable";

export function SitesList({ data }: { data: TenantData }) {
  const { t } = useI18n();
  const { role } = useSession();
  const [creating, setCreating] = useState(false);
  // Fleet managers add sites under an existing one; only tenant admins create root sites.
  const canCreate = isTenantAdmin(role) || (canManageFleet(role) && data.sites.length > 0);
  return (
    <>
      <PageHeader
        title={t("sites.title")}
        subtitle={t("sites.subtitle", { count: data.sites.length })}
        actions={
          canCreate && (
            <button type="button" className="button button-primary" onClick={() => setCreating(true)}>
              <Icon name="plus" size={16} />
              {t("sites.add")}
            </button>
          )
        }
      />
      <Panel flush>
        <SitesTable sites={data.sites} vehicles={data.vehicles} />
      </Panel>
      {creating && <SiteFormDialog data={data} onClose={() => setCreating(false)} />}
    </>
  );
}

type SiteModal = "edit" | "child" | "vehicle" | "delete" | null;

export function SiteDetail({ data, siteId }: { data: TenantData; siteId: string }) {
  const { t } = useI18n();
  const { role } = useSession();
  const [modal, setModal] = useState<SiteModal>(null);
  const site = data.sites.find((s) => s.id === siteId);
  if (!site) {
    return (
      <EmptyState icon="building" title={t("site.notFound")} action={<a href="#/sites" className="button button-secondary">{t("sites.title")}</a>} />
    );
  }
  const children = data.sites.filter((s) => s.parentId === site.id);
  const direct = data.vehicles.filter((v) => v.siteId === site.id);
  const all = vehiclesUnder(data.sites, data.vehicles, site.id);
  const parents = sitePath(data.sites, site.id).slice(0, -1);

  return (
    <>
      <PageHeader
        eyebrow={
          <>
            <span className="kind-tag">{t(`siteKind.${site.kind}`)}</span>
            {parents.length > 0 && <span className="eyebrow-path">{parents.map((p) => p.name).join(" / ")}</span>}
          </>
        }
        title={site.name}
        subtitle={site.address ?? t("site.noAddress")}
        actions={
          canManageFleet(role) && (
            <>
              <button type="button" className="button button-secondary button-sm" onClick={() => setModal("child")}>
                <Icon name="plus" size={15} />
                {t("site.addChild")}
              </button>
              <button type="button" className="button button-secondary button-sm" onClick={() => setModal("vehicle")}>
                <Icon name="truck" size={15} />
                {t("vehicles.add")}
              </button>
              <button type="button" className="button button-secondary button-sm" onClick={() => setModal("edit")}>
                <Icon name="edit" size={15} />
                {t("common.edit")}
              </button>
              {isTenantAdmin(role) && (
                <button type="button" className="button button-secondary button-sm button-danger-text" onClick={() => setModal("delete")}>
                  <Icon name="trash" size={15} />
                  {t("common.delete")}
                </button>
              )}
            </>
          )
        }
      />
      {modal === "edit" && <SiteFormDialog data={data} site={site} onClose={() => setModal(null)} />}
      {modal === "child" && <SiteFormDialog data={data} defaultParentId={site.id} onClose={() => setModal(null)} />}
      {modal === "vehicle" && <VehicleFormDialog data={data} defaultSiteId={site.id} onClose={() => setModal(null)} />}
      {modal === "delete" && <DeleteSiteDialog data={data} site={site} onClose={() => setModal(null)} />}

      {children.length > 0 && (
        <Panel title={t("site.childSites")} meta={<span className="mono">{children.length}</span>} flush>
          <SitesTable sites={data.sites} vehicles={data.vehicles} rootIds={children.map((c) => c.id)} />
        </Panel>
      )}

      <Panel title={t("site.vehiclesTitle")} meta={t("site.vehiclesSummary", { direct: direct.length, total: all.length })} flush>
        {all.length === 0 ? (
          <p className="table-empty">{t("site.noVehicles")}</p>
        ) : (
          <VehicleTable vehicles={all} sites={data.sites} showSite={children.length > 0} />
        )}
      </Panel>

      <SiteGeofencesPanel key={`zones-${site.id}`} data={data} site={site} />

      <SiteAlertsPanel key={site.id} data={data} site={site} />

      <SiteChecklistPanel key={`checklist-${site.id}`} data={data} site={site} />
    </>
  );
}
